import React, { Component } from 'react';
import moment from 'moment';
import API from '../../utils/API';
import { PIPELINE } from './components/VenuePipelineStrip';
import DateInput from './components/DateInput';

const fmt = (date, format = 'YYYY-MM-DD') => date ? moment(date).format(format) : '';

const TABS = [
	{ key: 'proposal',  label: 'Proposal',         icon: 'la-file-invoice-dollar' },
	{ key: 'sourcing',  label: 'Sourcing Details',  icon: 'la-clipboard-list' },
	{ key: 'notes',     label: 'Notes & Docs',      icon: 'la-sticky-note' },
	{ key: 'venue',     label: 'Venue Info',        icon: 'la-hotel' },
	// { key: 'history',   label: 'History',           icon: 'la-history' }
];

/* Normalize status to a plain string whether the API returns a string or an object */
const normalizeStatus = (s) => {
	if (!s) return 'Draft';
	if (typeof s === 'string') return s;
	return s.value || 'Draft';
};

/* Basic URL validation — must start with http:// or https:// */
const isValidUrl = (url) => {
	if (!url) return true; // empty is fine
	return /^https?:\/\/.+/.test(url.trim());
};

class VenueDetail extends Component {
	constructor(props) {
		super(props);
		const normalized = {
			...props.proposalVenue,
			status: normalizeStatus(props.proposalVenue?.status)
		};
		this.state = {
			proposalVenue: normalized,
			edited: { ...normalized },
			editing: false,
			saving: false,
			activeTab: 'proposal',
			urlError: ''
		};
	}

	componentDidUpdate(prevProps) {
		if (prevProps.proposalVenue._id !== this.props.proposalVenue._id) {
			const normalized = {
				...this.props.proposalVenue,
				status: normalizeStatus(this.props.proposalVenue?.status)
			};
			this.setState({
				proposalVenue: normalized,
				edited: { ...normalized },
				editing: false,
				activeTab: 'proposal',
				urlError: ''
			});
		}
	}

	updateField = (field, value) => {
		const updates = {
			edited: { ...this.state.edited, [field]: value },
			editing: true
		};
		// Live-validate URL field
		if (field === 'proposalUrl') {
			updates.urlError = value && !isValidUrl(value)
				? 'Please enter a valid URL starting with http:// or https://'
				: '';
		}
		this.setState(updates);
	};

	cancel = () => {
		this.setState({
			edited: { ...this.state.proposalVenue },
			editing: false
		});
	};

	setStatus = (next) => {
		const { edited } = this.state;
		const updates = { status: next };
		if (next === 'Sent' && !edited.sentDate) updates.sentDate = new Date().toISOString();
		if (next === 'Received' && !edited.responseDate) updates.responseDate = new Date().toISOString();
		if (next === 'Contracted' && !edited.contractedDate) updates.contractedDate = new Date().toISOString();
		this.setState({ edited: { ...edited, ...updates }, editing: true });
	};

	save = async () => {
		const { edited } = this.state;
		const { orgId, updateVenue, requestId } = this.props;

		// Block save if URL is invalid
		if (edited.proposalUrl && !isValidUrl(edited.proposalUrl)) {
			this.setState({ urlError: 'Please enter a valid URL starting with http:// or https://' });
			return;
		}

		this.setState({ saving: true, urlError: '' });
		try {
			// status is already a plain string — send as-is
			await API().patch(`Organizations/${orgId}/proposal-venues/${edited._id}`, { ...edited });
			this.setState({
				proposalVenue: { ...edited },
				editing: false,
				saving: false
			});
			updateVenue && updateVenue(edited._id, { ...edited });

			// If contracted, auto-mark request as Awarded
			if (edited.status === 'Contracted') {
				await API().patch(
					`Organizations/${orgId}/proposal-requests/${requestId}`,
					{ status: 'Awarded', awardedVenueId: edited.sourcedVenueId }
				);
			}
		} catch (e) {
			console.error('Error saving venue:', e);
			this.setState({ saving: false });
		}
	};

	confirmRemove = () => {
		if (window.confirm('Remove this venue from the proposal request?')) {
			this.props.removeVenue && this.props.removeVenue(this.state.proposalVenue._id);
		}
	};

	// ── Peak Period helpers ──────────────────────────────────────────────────
	addPeakPeriod = () => {
		const periods = [...(this.state.edited.peakPeriods || [])];
		periods.push({ arrivalDate: '', departureDate: '', roomTypes: [], suiteRate: '', staffRate: '', resortFee: '' });
		this.setState({ edited: { ...this.state.edited, peakPeriods: periods }, editing: true });
	};

	removePeakPeriod = (idx) => {
		const periods = [...(this.state.edited.peakPeriods || [])];
		periods.splice(idx, 1);
		this.setState({ edited: { ...this.state.edited, peakPeriods: periods }, editing: true });
	};

	updatePeakPeriod = (idx, field, value) => {
		const periods = [...(this.state.edited.peakPeriods || [])];
		periods[idx] = { ...periods[idx], [field]: value };
		this.setState({ edited: { ...this.state.edited, peakPeriods: periods }, editing: true });
	};

	addRoomType = (periodIdx) => {
		const periods = [...(this.state.edited.peakPeriods || [])];
		const roomTypes = [...(periods[periodIdx].roomTypes || [])];
		roomTypes.push({ type: '', rate: '', count: '' });
		periods[periodIdx] = { ...periods[periodIdx], roomTypes };
		this.setState({ edited: { ...this.state.edited, peakPeriods: periods }, editing: true });
	};

	removeRoomType = (periodIdx, rtIdx) => {
		const periods = [...(this.state.edited.peakPeriods || [])];
		const roomTypes = [...(periods[periodIdx].roomTypes || [])];
		roomTypes.splice(rtIdx, 1);
		periods[periodIdx] = { ...periods[periodIdx], roomTypes };
		this.setState({ edited: { ...this.state.edited, peakPeriods: periods }, editing: true });
	};

	updateRoomType = (periodIdx, rtIdx, field, value) => {
		const periods = [...(this.state.edited.peakPeriods || [])];
		const roomTypes = [...(periods[periodIdx].roomTypes || [])];
		roomTypes[rtIdx] = { ...roomTypes[rtIdx], [field]: value };
		periods[periodIdx] = { ...periods[periodIdx], roomTypes };
		this.setState({ edited: { ...this.state.edited, peakPeriods: periods }, editing: true });
	};

	renderSourcingTab() {
		const { edited } = this.state;
		const peakPeriods = edited.peakPeriods || [];

		const sectionHead = (title) => (
			<h3 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 12px', color: '#1a1a1a' }}>{title}</h3>
		);
		const divider = () => (
			<hr style={{ border: 'none', borderTop: '1px solid #f0f0f0', margin: '4px 0 24px' }} />
		);

		return (
			<>
				{/* ── General ── */}
				{sectionHead('General')}
				<div className="pr-two-col" style={{ marginBottom: 16 }}>
					<div className="pr-field-group compact">
						<label className="pr-field-label">Hotel Website</label>
						<input
							className="pr-text-input"
							type="url"
							value={edited.hotelWebsite || ''}
							onChange={(e) => this.updateField('hotelWebsite', e.target.value)}
							placeholder="https://..."
						/>
					</div>
					<div className="pr-field-group compact">
						<label className="pr-field-label">Total Number of Guest Rooms</label>
						<input
							className="pr-text-input"
							type="number"
							value={edited.totalGuestRooms || ''}
							onChange={(e) => this.updateField('totalGuestRooms', parseInt(e.target.value) || null)}
							placeholder="0"
						/>
					</div>
				</div>

				<div className="pr-two-col" style={{ marginBottom: 0 }}>
					<div className="pr-field-group compact">
						<label className="pr-field-label">Rooms &amp; Space on Hold</label>
						<div className="pr-select-wrap">
							<select
								className="pr-select"
								value={edited.roomsOnHold || ''}
								onChange={(e) => this.updateField('roomsOnHold', e.target.value)}
							>
								<option value="">— Select —</option>
								<option value="Yes">Yes</option>
								<option value="No">No</option>
							</select>
							<i className="las la-angle-down pr-select-arrow" />
						</div>
					</div>
					<div className="pr-field-group compact">
						<label className="pr-field-label">Proposal Expiration Date</label>
						<div className="rfp-date-input-wrap">
							<DateInput
								className="rfp-date-input"
								value={fmt(edited.proposalExpirationDate)}
								onChange={(val) => this.updateField('proposalExpirationDate', val || null)}
							/>
							<i className="las la-calendar rfp-date-icon" />
						</div>
					</div>
				</div>

				{divider()}

				{/* ── Peak Periods ── */}
				<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
					{sectionHead('Peak Periods')}
					<button
						type="button"
						onClick={this.addPeakPeriod}
						style={{
							display: 'inline-flex', alignItems: 'center', gap: 6,
							background: 'none', border: '1px solid #d0d0d0', borderRadius: 6,
							padding: '5px 12px', fontSize: 12, fontWeight: 600, color: '#1a1a1a',
							cursor: 'pointer', marginBottom: 12, whiteSpace: 'nowrap'
						}}
					>
						<i className="las la-plus" style={{ fontSize: 14 }} />
						Add Peak Period
					</button>
				</div>

				{peakPeriods.length === 0 && (
					<p style={{ fontSize: 13, color: '#aaa', marginBottom: 20 }}>
						No peak periods yet. Click "Add Peak Period" to add arrival / departure dates and rates.
					</p>
				)}

				{peakPeriods.map((period, pIdx) => (
					<div
						key={pIdx}
						style={{
							border: '1px solid #e8e8e8', borderRadius: 8, padding: 16,
							marginBottom: 16, background: '#fafafa'
						}}
					>
						{/* Period header */}
						<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
							<span style={{ fontSize: 13, fontWeight: 700, color: '#444' }}>
								Period {pIdx + 1}
							</span>
							<button
								type="button"
								onClick={() => this.removePeakPeriod(pIdx)}
								style={{
									background: 'none', border: 'none', color: '#d63939',
									cursor: 'pointer', padding: 0, fontSize: 12,
									display: 'inline-flex', alignItems: 'center', gap: 4
								}}
							>
								<i className="las la-times" />
								Remove
							</button>
						</div>

						{/* Arrival / Departure */}
						<div className="pr-two-col" style={{ marginBottom: 12 }}>
							<div className="pr-field-group compact">
								<label className="pr-field-label">Peak Arrival</label>
								<div className="rfp-date-input-wrap">
									<DateInput
										className="rfp-date-input"
										value={period.arrivalDate || ''}
										onChange={(val) => this.updatePeakPeriod(pIdx, 'arrivalDate', val || '')}
									/>
									<i className="las la-calendar rfp-date-icon" />
								</div>
							</div>
							<div className="pr-field-group compact">
								<label className="pr-field-label">Peak Departure</label>
								<div className="rfp-date-input-wrap">
									<DateInput
										className="rfp-date-input"
										value={period.departureDate || ''}
										onChange={(val) => this.updatePeakPeriod(pIdx, 'departureDate', val || '')}
									/>
									<i className="las la-calendar rfp-date-icon" />
								</div>
							</div>
						</div>

						{/* Room Types */}
						<div className="pr-field-group compact" style={{ marginBottom: 8 }}>
							<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
								<label className="pr-field-label" style={{ margin: 0 }}>Room Types &amp; Rates</label>
								<button
									type="button"
									onClick={() => this.addRoomType(pIdx)}
									style={{
										background: 'none', border: '1px solid #d0d0d0', borderRadius: 5,
										padding: '3px 10px', fontSize: 11, fontWeight: 600, color: '#555',
										cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4
									}}
								>
									<i className="las la-plus" style={{ fontSize: 12 }} />
									Add Room Type
								</button>
							</div>

							{(period.roomTypes || []).length === 0 && (
								<p style={{ fontSize: 12, color: '#bbb', margin: '4px 0 0' }}>No room types added.</p>
							)}

							{(period.roomTypes || []).map((rt, rtIdx) => (
								<div
									key={rtIdx}
									style={{
										display: 'grid',
										gridTemplateColumns: '1fr 90px 70px 28px',
										gap: 6, alignItems: 'center', marginBottom: 6
									}}
								>
									<input
										className="pr-text-input"
										type="text"
										value={rt.type || ''}
										onChange={(e) => this.updateRoomType(pIdx, rtIdx, 'type', e.target.value)}
										placeholder="Room type (e.g. King Deluxe)"
									/>
									<div className="pr-currency-wrap" style={{ marginBottom: 0 }}>
										<span className="pr-currency-symbol" style={{ fontSize: 14 }}>$</span>
										<input
											className="pr-text-input"
											type="number"
											value={rt.rate || ''}
											onChange={(e) => this.updateRoomType(pIdx, rtIdx, 'rate', parseFloat(e.target.value) || '')}
											placeholder="Rate"
										/>
									</div>
									<input
										className="pr-text-input"
										type="number"
										value={rt.count || ''}
										onChange={(e) => this.updateRoomType(pIdx, rtIdx, 'count', parseInt(e.target.value) || '')}
										placeholder="# Rooms"
									/>
									<button
										type="button"
										onClick={() => this.removeRoomType(pIdx, rtIdx)}
										style={{
											background: 'none', border: 'none', color: '#bbb',
											cursor: 'pointer', padding: 0, fontSize: 16,
											display: 'flex', alignItems: 'center', justifyContent: 'center'
										}}
										title="Remove room type"
									>
										<i className="las la-times-circle" />
									</button>
								</div>
							))}
						</div>

						{/* Suite / Staff / Resort */}
						<div className="pr-three-col" style={{ marginBottom: 0 }}>
							<div className="pr-field-group compact">
								<label className="pr-field-label">Suite Rate</label>
								<div className="pr-currency-wrap">
									<span className="pr-currency-symbol" style={{ fontSize: 16 }}>$</span>
									<input
										className="pr-text-input"
										type="number"
										value={period.suiteRate || ''}
										onChange={(e) => this.updatePeakPeriod(pIdx, 'suiteRate', parseFloat(e.target.value) || '')}
										placeholder="0"
									/>
								</div>
							</div>
							<div className="pr-field-group compact">
								<label className="pr-field-label">Staff Rate</label>
								<div className="pr-currency-wrap">
									<span className="pr-currency-symbol" style={{ fontSize: 16 }}>$</span>
									<input
										className="pr-text-input"
										type="number"
										value={period.staffRate || ''}
										onChange={(e) => this.updatePeakPeriod(pIdx, 'staffRate', parseFloat(e.target.value) || '')}
										placeholder="0"
									/>
								</div>
							</div>
							<div className="pr-field-group compact">
								<label className="pr-field-label">Resort / Destination Fee</label>
								<input
									className="pr-text-input"
									type="text"
									value={period.resortFee || ''}
									onChange={(e) => this.updatePeakPeriod(pIdx, 'resortFee', e.target.value)}
									placeholder="e.g. $35/night"
								/>
							</div>
						</div>
					</div>
				))}

				{divider()}

				{/* ── Rates & Fees ── */}
				{sectionHead('Rates & Fees')}
				<div className="pr-two-col" style={{ marginBottom: 0 }}>
					<div className="pr-field-group compact">
						<label className="pr-field-label">Room Tax / Other Mandatory Fees</label>
						<input
							className="pr-text-input"
							type="text"
							value={edited.roomTaxFees || ''}
							onChange={(e) => this.updateField('roomTaxFees', e.target.value)}
							placeholder="e.g. 14% tax + $12 occupancy fee"
						/>
					</div>
					<div className="pr-field-group compact">
						<label className="pr-field-label">Function Space Rental</label>
						<div className="pr-currency-wrap">
							<span className="pr-currency-symbol" style={{ fontSize: 16 }}>$</span>
							<input
								className="pr-text-input"
								type="number"
								value={edited.functionSpaceRental || ''}
								onChange={(e) => this.updateField('functionSpaceRental', parseFloat(e.target.value) || null)}
								placeholder="0"
							/>
						</div>
					</div>
				</div>

				{divider()}

				{/* ── Space Assignments ── */}
				{sectionHead('Space Assignments')}
				<div className="pr-field-group compact">
					<label className="pr-field-label">
						Meeting Room Assignments
						<span className="pr-field-hint">Include sq footage</span>
					</label>
					<textarea
						className="pr-textarea"
						value={edited.meetingRoomAssignments || ''}
						onChange={(e) => this.updateField('meetingRoomAssignments', e.target.value)}
						placeholder="e.g. Ballroom A (4,200 sq ft) — General Sessions&#10;Salon 1 (800 sq ft) — Breakout"
						rows={4}
					/>
				</div>
				<div className="pr-field-group compact">
					<label className="pr-field-label">Office Space Assignments</label>
					<textarea
						className="pr-textarea"
						value={edited.officeSpaceAssignments || ''}
						onChange={(e) => this.updateField('officeSpaceAssignments', e.target.value)}
						placeholder="e.g. Boardroom (500 sq ft) — Staff Office"
						rows={3}
					/>
				</div>
				<div className="pr-field-group compact">
					<label className="pr-field-label">F&amp;B Functions Assignments</label>
					<textarea
						className="pr-textarea"
						value={edited.fbFunctionsAssignments || ''}
						onChange={(e) => this.updateField('fbFunctionsAssignments', e.target.value)}
						placeholder="e.g. Grand Ballroom — Welcome Reception (Day 1)"
						rows={3}
					/>
				</div>
				<div className="pr-field-group compact">
					<label className="pr-field-label">Other Space</label>
					<textarea
						className="pr-textarea"
						value={edited.otherSpace || ''}
						onChange={(e) => this.updateField('otherSpace', e.target.value)}
						placeholder="Any additional space assignments or notes..."
						rows={3}
					/>
				</div>

				{divider()}

				{/* ── Location Info ── */}
				{sectionHead('Location Info')}
				<div className="pr-two-col" style={{ marginBottom: 0 }}>
					<div className="pr-field-group compact">
						<label className="pr-field-label">Distance to Restaurants &amp; Bars</label>
						<input
							className="pr-text-input"
							type="text"
							value={edited.distanceToRestaurants || ''}
							onChange={(e) => this.updateField('distanceToRestaurants', e.target.value)}
							placeholder="e.g. 0.2 miles — walkable"
						/>
					</div>
					<div className="pr-field-group compact">
						<label className="pr-field-label">Airport Distance</label>
						<input
							className="pr-text-input"
							type="text"
							value={edited.airportDistance || ''}
							onChange={(e) => this.updateField('airportDistance', e.target.value)}
							placeholder="e.g. 12 miles — 20 min drive"
						/>
					</div>
				</div>

				{divider()}

				{/* ── Additional Info ── */}
				{sectionHead('Additional Info')}
				<div className="pr-field-group compact">
					<label className="pr-field-label">Recent Renovations</label>
					<div className="pr-field-sublabel">Meeting space and guest rooms</div>
					<textarea
						className="pr-textarea"
						value={edited.recentRenovations || ''}
						onChange={(e) => this.updateField('recentRenovations', e.target.value)}
						placeholder="e.g. Guest rooms renovated 2023; ballroom AV upgraded 2024"
						rows={3}
					/>
				</div>
				<div className="pr-field-group">
					<label className="pr-field-label">Concessions</label>
					<textarea
						className="pr-textarea"
						value={edited.concessions || ''}
						onChange={(e) => this.updateField('concessions', e.target.value)}
						placeholder="e.g. Complimentary suite for planner, 1 per 40 comps, Wi-Fi included in package..."
						rows={4}
					/>
				</div>
			</>
		);
	}

	renderTab() {
		const { edited, proposalVenue, activeTab, urlError } = this.state;
		const venue = proposalVenue.venue || {};
		const history = proposalVenue.statusHistory || [];

		if (activeTab === 'sourcing') {
			return this.renderSourcingTab();
		}

		if (activeTab === 'proposal') {
			
			const currentStatus = edited.status || 'Draft';
			const currentDef = PIPELINE.find(p => p.key === currentStatus) || PIPELINE[0];
			return (
				<>
					{/* Pipeline segmented control */}
					{/* <div className="pr-pipeline">
						{PRIMARY_STATUS_KEYS.map(key => {
							const def = PIPELINE.find(p => p.key === key);
							const active = key === currentStatus;
							return (
								<button
									key={key}
									type="button"
									className={`pr-pipeline-seg ${active ? 'active' : ''}`}
									onClick={() => this.setStatus(key)}
									style={active ? { background: def.color } : undefined}
								>
									{def.label}
								</button>
							);
						})}
					</div> */}

					{/* Secondary statuses (Turndown, Cancel) */}
					{/* <div className="pr-secondary-statuses">
						<span className="pr-secondary-label">Other:</span>
						{SECONDARY_STATUS_KEYS.map(key => {
							const def = PIPELINE.find(p => p.key === key);
							const active = key === currentStatus;
							return (
								<button
									key={key}
									type="button"
									className="pr-status-chip"
									onClick={() => this.setStatus(key)}
									style={
										active
											? { background: `${def.color}1a`, borderColor: def.color, color: def.color }
											: undefined
									}
								>
									{def.label}
								</button>
							);
						})}
						<span style={{ marginLeft: 'auto', fontSize: 12, color: '#888' }}>
							Current: <strong style={{ color: currentDef.color }}>{currentDef.label}</strong>
						</span>
					</div> */}
					<div className="pr-field-group">
						<label className="pr-field-label">Status</label>
						<div className="pr-select-wrap">
							<select
								className="pr-select"
								value={currentStatus}
								onChange={(e) => this.setStatus(e.target.value)}
								style={{ color: currentDef.color, fontWeight: 600 }}
							>
								{PIPELINE.map(p => (
									<option key={p.key} value={p.key}>{p.label}</option>
								))}
							</select>
							<i className="las la-angle-down pr-select-arrow" />
						</div>
					</div>

					<h3 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 12px', color: '#1a1a1a' }}>Timeline</h3>
					<div className="pr-three-col" style={{ marginBottom: 24 }}>
						<div className="pr-field-group compact">
							<label className="pr-field-label">Date Sent</label>
							<div className="rfp-date-input-wrap">
								<DateInput className="rfp-date-input" value={fmt(edited.sentDate)} onChange={(val) => this.updateField('sentDate', val || null)} />
								<i className="las la-calendar rfp-date-icon" />
							</div>
						</div>
						<div className="pr-field-group compact">
							<label className="pr-field-label">Response Date</label>
							<div className="rfp-date-input-wrap">
								<DateInput className="rfp-date-input" value={fmt(edited.responseDate)} onChange={(val) => this.updateField('responseDate', val || null)} />
								<i className="las la-calendar rfp-date-icon" />
							</div>
						</div>
						<div className="pr-field-group compact">
							<label className="pr-field-label">Contracted Date</label>
							<div className="rfp-date-input-wrap">
								<DateInput className="rfp-date-input" value={fmt(edited.contractedDate)} onChange={(val) => this.updateField('contractedDate', val || null)} />
								<i className="las la-calendar rfp-date-icon" />
							</div>
						</div>
					</div>

					<h3 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 12px', color: '#1a1a1a' }}>Proposal Response</h3>
					<div className="pr-two-col">
						<div className="pr-field-group compact">
							<label className="pr-field-label">Quoted Room Rate</label>
							<div className="pr-currency-wrap">
								<span className="pr-currency-symbol" style={{ fontSize: 16 }}>$</span>
								<input className="pr-text-input" type="number" value={edited.quotedRoomRate || ''} onChange={(e) => this.updateField('quotedRoomRate', parseFloat(e.target.value) || null)} placeholder="0" />
							</div>
						</div>
						<div className="pr-field-group compact">
							<label className="pr-field-label">Meeting Space Cost</label>
							<div className="pr-currency-wrap">
								<span className="pr-currency-symbol" style={{ fontSize: 16 }}>$</span>
								<input className="pr-text-input" type="number" value={edited.quotedMeetingSpaceCost || ''} onChange={(e) => this.updateField('quotedMeetingSpaceCost', parseFloat(e.target.value) || null)} placeholder="0" />
							</div>
						</div>
						<div className="pr-field-group compact">
							<label className="pr-field-label">F&amp;B Minimum</label>
							<div className="pr-currency-wrap">
								<span className="pr-currency-symbol" style={{ fontSize: 16 }}>$</span>
								<input className="pr-text-input" type="number" value={edited.quotedFBMinimum || ''} onChange={(e) => this.updateField('quotedFBMinimum', parseFloat(e.target.value) || null)} placeholder="0" />
							</div>
						</div>
						<div className="pr-field-group compact">
							<label className="pr-field-label">Contract Value</label>
							<div className="pr-currency-wrap">
								<span className="pr-currency-symbol" style={{ fontSize: 16 }}>$</span>
								<input className="pr-text-input" type="number" value={edited.contractValue || ''} onChange={(e) => this.updateField('contractValue', parseFloat(e.target.value) || null)} placeholder="0" />
							</div>
						</div>
					</div>

					<div className="pr-field-group">
						<label className="pr-field-label">Additional Details</label>
						<textarea
							className="pr-textarea"
							value={edited.additionalDetails || ''}
							onChange={(e) => this.updateField('additionalDetails', e.target.value)}
							placeholder="Any additional details about this venue's proposal response..."
							rows={4}
						/>
					</div>
				</>
			);
		}

		if (activeTab === 'notes') {
			return (
				<>
					<div className="pr-field-group">
						<label className="pr-field-label">Internal Notes</label>
						<textarea
							className="pr-textarea"
							value={edited.internalNotes || ''}
							onChange={(e) => this.updateField('internalNotes', e.target.value)}
							placeholder="Add notes about this venue's proposal, site visit, conversations..."
							rows={8}
						/>
					</div>
					<div className="pr-field-group">
						<label className="pr-field-label">Proposal Document URL</label>
						<input
							className="pr-text-input"
							value={edited.proposalUrl || ''}
							onChange={(e) => this.updateField('proposalUrl', e.target.value)}
							placeholder="https://..."
							style={urlError ? { borderBottomColor: '#d63939' } : undefined}
						/>
						{urlError && (
							<span style={{ fontSize: 12, color: '#d63939', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
								<i className="las la-exclamation-circle" />
								{urlError}
							</span>
						)}
					</div>
					{edited.proposalUrl && !urlError && (
						<a
							href={edited.proposalUrl}
							target="_blank"
							rel="noopener noreferrer"
							style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#1a1a1a', fontSize: 13, marginTop: 4 }}
						>
							<i className="las la-external-link-alt" />
							Open proposal document
						</a>
					)}
				</>
			);
		}

		if (activeTab === 'venue') {
			const hasInfo = venue.description || venue.capacity?.max || venue.totalRooms
				|| venue.amenities?.length > 0 || venue.contactEmail || venue.phoneNumber
				|| venue.website || venue.rating > 0;
			if (!hasInfo) {
				return <p style={{ color: '#888' }}>No additional venue information on file.</p>;
			}
			return (
				<>
					{venue.description && (
						<p style={{ color: '#555', lineHeight: 1.6, marginBottom: 20 }}>{venue.description}</p>
					)}

					{/* Key stats row */}
					<div className="pr-three-col" style={{ marginBottom: 20 }}>
						{venue.capacity?.max > 0 && (
							<div>
								<div style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Max Capacity</div>
								<div style={{ fontSize: 14, fontWeight: 600 }}>{venue.capacity.max.toLocaleString()} guests</div>
							</div>
						)}
						{venue.totalRooms > 0 && (
							<div>
								<div style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Guest Rooms</div>
								<div style={{ fontSize: 14, fontWeight: 600 }}>{venue.totalRooms}</div>
							</div>
						)}
						{venue.rating > 0 && (
							<div>
								<div style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Rating</div>
								<div style={{ fontSize: 14, fontWeight: 600 }}>
									{venue.rating.toFixed(1)}
									{venue.totalReviews > 0 && (
										<span style={{ fontWeight: 400, color: '#888', fontSize: 12, marginLeft: 4 }}>
											({venue.totalReviews.toLocaleString()} reviews)
										</span>
									)}
								</div>
							</div>
						)}
					</div>

					{venue.amenities?.length > 0 && (
						<div style={{ marginBottom: 20 }}>
							<div style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Amenities</div>
							<div style={{ fontSize: 14 }}>{venue.amenities.join(' · ')}</div>
						</div>
					)}

					{(venue.website || venue.contactEmail || venue.phoneNumber) && (
						<div className="pr-two-col">
							{venue.website && (
								<div>
									<div style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Website</div>
									<a href={venue.website} target="_blank" rel="noopener noreferrer" style={{ fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
										<i className="las la-globe" />
										Visit website
									</a>
								</div>
							)}
							{venue.contactEmail && (
								<div>
									<div style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Contact Email</div>
									<a href={`mailto:${venue.contactEmail}`} style={{ fontSize: 14 }}>{venue.contactEmail}</a>
								</div>
							)}
							{venue.phoneNumber && (
								<div>
									<div style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Phone</div>
									<a href={`tel:${venue.phoneNumber}`} style={{ fontSize: 14 }}>{venue.phoneNumber}</a>
								</div>
							)}
						</div>
					)}
				</>
			);
		}

		if (activeTab === 'history') {
			if (history.length === 0) {
				return <p style={{ color: '#888' }}>No status changes yet. Update the status above to start tracking.</p>;
			}
			return (
				<div>
					{history.map((entry, i) => {
						const def = PIPELINE.find(p => p.key === entry.status);
						const color = def ? def.color : '#868e96';
						return (
							<div
								key={i}
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: 12,
									padding: '12px 0',
									borderBottom: i < history.length - 1 ? '1px solid #f0f0f0' : 'none'
								}}
							>
								<span style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
								<span style={{ fontSize: 14, fontWeight: 600 }}>{def ? def.label : entry.status}</span>
								<span style={{ fontSize: 12, color: '#888', marginLeft: 'auto' }}>
									{moment(entry.date).format('MMM D, YYYY')}
								</span>
								{entry.notes && <span style={{ fontSize: 12, color: '#888' }}>{entry.notes}</span>}
							</div>
						);
					})}
				</div>
			);
		}

		return null;
	}

	render() {
		const { proposalVenue, edited, editing, saving, activeTab } = this.state;
		const { onClose } = this.props;
		const venue = proposalVenue.venue || {};

		return (
			<>
				{/* Header */}
				<div className="pr-overlay-header">
					<div className="pr-overlay-title-row">
						<div style={{ flex: 1, minWidth: 0 }}>
							<h2 className="pr-overlay-venue-name">{venue.name || 'Venue'}</h2>
							<div className="pr-overlay-venue-meta">
								{venue.location && (
									<span><i className="las la-map-marker-alt " /> {venue.location}</span>
								)}
								{venue.website && (
									<a href={venue.website} target="_blank" rel="noopener noreferrer" style={{ color: '#1a1a1a', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
										<i className="las la-globe" />
										Website
									</a>
								)}
								<button
									type="button"
									onClick={this.confirmRemove}
									style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#d63939', cursor: 'pointer', padding: 0, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 4 }}
								>
									<i className="las la-trash-alt" />
									Remove
								</button>
							</div>
						</div>
						<button className="pr-overlay-close" onClick={onClose} aria-label="Close">
							<i className="las la-times" />
						</button>
					</div>

					

					{/* Tabs */}
					<div className="pr-tab-bar">
						{TABS.map(tab => (
							<button
								key={tab.key}
								type="button"
								className={`pr-tab ${activeTab === tab.key ? 'active' : ''}`}
								onClick={() => this.setState({ activeTab: tab.key })}
							>
								<i className={`las ${tab.icon}`} />
								{tab.label}
							</button>
						))}
					</div>
				</div>

				{/* Body */}
				<div className="pr-overlay-body">
					{this.renderTab()}
				</div>

				{/* Footer */}
				<div className={`pr-overlay-footer ${editing ? 'editing' : ''}`}>
					{proposalVenue.updatedAt && !editing && (
						<span style={{ fontSize: 12, color: '#888', marginRight: 'auto' }}>
							Updated {moment(proposalVenue.updatedAt).fromNow()}
						</span>
					)}
					{editing && (
						<>
							<button className="pr-btn-cancel" onClick={this.cancel} disabled={saving}>
								Cancel
							</button>
							<button className="pr-btn-save" onClick={this.save} disabled={saving}>
								{saving ? 'Saving…' : 'Save Changes'}
							</button>
						</>
					)}
					{!editing && (
						<button className="pr-btn-cancel" onClick={onClose}>
							Close
						</button>
					)}
				</div>
			</>
		);
	}
}

export default VenueDetail;
